import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../../utils/api';

// The lookup takes seconds to a minute (Prism spec kg_quote_path §8): while any
// card is pending the hook polls; once none is, it stops until the next refresh.
export const POLL_MS = 20000;

/**
 * The identity cards for one deal, keyed by quote_item_id.
 *
 * `version` is the page's refresh key: bump it after a verdict or an item edit
 * to refetch. The hook never throws — a failed read leaves `cardsById` as it
 * was and surfaces `error`, so the card shows "unavailable" rather than the
 * page breaking.
 */
export function useItemIdentifications(dealId, version = 0) {
    const [cardsById, setCardsById] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);
    const genRef = useRef(0);

    const refresh = useCallback(() => setTick(t => t + 1), []);

    useEffect(() => {
        if (!dealId) { setCardsById({}); return undefined; }
        const gen = ++genRef.current;
        let timer = null;
        setLoading(true);
        api.get(`/api/deals/${dealId}/identifications`)
            .then(res => {
                if (gen !== genRef.current) return;
                const next = {};
                for (const card of res?.items || []) next[card.quote_item_id] = card;
                setCardsById(next);
                setError(null);
                if (Object.values(next).some(c => c.pending)) timer = setTimeout(() => setTick(t => t + 1), POLL_MS);
            })
            .catch(e => { if (gen === genRef.current) setError(e.message || 'identification unavailable'); })
            .finally(() => { if (gen === genRef.current) setLoading(false); });
        return () => { if (timer) clearTimeout(timer); };
    }, [dealId, version, tick]);

    return { cardsById, loading, error, refresh };
}
